import { useState } from "react";

const budgetTiers = [
  {
    label: "Under $200",
    items: [
      { name: "IKEA MALM Bed Frame", price: "$179", note: "White stained oak veneer, clean lines" },
      { name: "H&M Home Washed Linen Duvet Cover", price: "$89", note: "Oat colour, pre-washed for softness" },
      { name: "Target Threshold Linen Curtains", price: "$45/panel", note: "Warm ivory, rod pocket" },
    ],
  },
  {
    label: "$200–$600",
    items: [
      { name: "West Elm Petal Bed Frame", price: "$499", note: "Natural oak, low profile, slatted" },
      { name: "Cultiver Linen Sheet Set", price: "$295", note: "Oat, heavyweight washed linen" },
      { name: "Pottery Barn Belgian Flax Curtains", price: "$149/panel", note: "Natural, floor-to-ceiling" },
    ],
  },
  {
    label: "$600+",
    items: [
      { name: "Crate & Barrel Tate Bed", price: "$1,299", note: "Solid oak, minimal headboard" },
      { name: "Parachute Linen Bundle", price: "$499", note: "Driftwood, full bedding set" },
      { name: "Serena & Lily Riviera Nightstand", price: "$698", note: "Natural rattan, warm tone" },
    ],
  },
];

const topPicks = [
  {
    name: "Washed Linen Duvet Cover",
    brand: "Cultiver",
    price: "$195",
    tag: "Editor's Pick",
    image: "https://public.readdy.ai/ai/img_res/93d9813b736447f2f345ca250ea2fefb.jpg",
  },
  {
    name: "Natural Oak Nightstand",
    brand: "West Elm",
    price: "$349",
    tag: "Best Value",
    image: "https://readdy.ai/api/search-image?query=natural%20oak%20wood%20nightstand%20simple%20minimal%20design%20product%20photography%20clean%20white%20background%20warm%20wood%20grain&width=480&height=480&seq=cbn_shop2&orientation=squarish",
  },
  {
    name: "Wool Bedroom Rug",
    brand: "Ruggable",
    price: "$289",
    tag: "Most Versatile",
    image: "https://readdy.ai/api/search-image?query=soft%20wool%20area%20rug%20warm%20ivory%20oat%20neutral%20minimal%20texture%20product%20photography%20clean%20background&width=480&height=480&seq=cbn_shop3&orientation=squarish",
  },
  {
    name: "Ceramic Table Lamp",
    brand: "CB2",
    price: "$129",
    tag: "Finishing Touch",
    image: "https://readdy.ai/api/search-image?query=ceramic%20table%20lamp%20warm%20white%20cream%20minimal%20design%20product%20photography%20clean%20background%20soft%20warm%20light&width=480&height=480&seq=cbn_shop4&orientation=squarish",
  },
];

const faqs = [
  {
    q: "What's the best linen bedding colour for a neutral bedroom?",
    a: "Oat and warm ivory are the most versatile. They work with any warm neutral wall colour and look beautiful in both morning and evening light. Avoid cool whites — they can feel clinical.",
  },
  {
    q: "Do I need a rug in the bedroom?",
    a: "Yes — a rug adds warmth, softness underfoot, and helps anchor the bed visually. Choose a size that extends at least 60cm beyond each side of the bed.",
  },
  {
    q: "How do I make a small bedroom feel calm and open?",
    a: "Keep furniture low-profile, use light wall colours, hang curtains high and wide, and keep surfaces clear. Mirrors can also help reflect light and make the space feel larger.",
  },
  {
    q: "What wood tone works best with a neutral bedroom palette?",
    a: "Natural oak and light walnut are ideal — they're warm without being too orange or yellow. Avoid very dark woods, which can feel heavy in a calm neutral space.",
  },
];

export default function CBNShopSection() {
  const [activeTier, setActiveTier] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <section className="bg-white">
      {/* Top Picks */}
      <div className="py-24 max-w-screen-xl mx-auto px-8 md:px-16">
        <div className="text-center mb-14">
          <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
            Shop the Guide
          </p>
          <h2 className="text-[#2C2825] text-3xl md:text-4xl mb-4" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
            Our Top Picks
          </h2>
          <p className="text-[#8B8378] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
            Carefully selected pieces that deliver on quality, warmth, and calm.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {topPicks.map((item) => (
            <div key={item.name} className="group cursor-pointer">
              <div className="relative w-full h-56 rounded-xl overflow-hidden bg-[#F5F1E8] mb-4">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <span
                  className="absolute top-3 left-3 bg-[#2C2825] text-white text-[9px] px-2.5 py-1 rounded-full tracking-[0.15em]"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {item.tag}
                </span>
              </div>
              <p className="text-[#B5A898] text-[10px] tracking-[0.15em] uppercase mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                {item.brand}
              </p>
              <h3 className="text-[#2C2825] text-sm mb-1" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500 }}>
                {item.name}
              </h3>
              <p className="text-[#6B5E53] text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                {item.price}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Budget Tiers */}
      <div className="py-20 bg-[#FAF7F2]">
        <div className="max-w-screen-xl mx-auto px-8 md:px-16">
          <div className="text-center mb-12">
            <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              By Budget
            </p>
            <h2 className="text-[#2C2825] text-3xl md:text-4xl" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
              Shop at Every Price Point
            </h2>
          </div>
          <div className="flex justify-center gap-2 mb-10">
            {budgetTiers.map((tier, i) => (
              <button
                key={tier.label}
                onClick={() => setActiveTier(i)}
                className={`px-5 py-2 rounded-full text-xs tracking-[0.12em] transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeTier === i
                    ? "bg-[#2C2825] text-white"
                    : "border border-[#C8BAA8] text-[#6B5E53] hover:bg-[#2C2825] hover:text-white hover:border-[#2C2825]"
                }`}
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {tier.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {budgetTiers[activeTier].items.map((item) => (
              <div key={item.name} className="bg-white rounded-xl p-6 border border-[#EDE6DA]">
                <p className="text-[#2C2825] text-sm font-medium mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {item.name}
                </p>
                <p className="text-[#9E8070] text-sm mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {item.price}
                </p>
                <p className="text-[#8B8378] text-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
                  {item.note}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="py-24 max-w-screen-xl mx-auto px-8 md:px-16">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              FAQ
            </p>
            <h2 className="text-[#2C2825] text-3xl md:text-4xl" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
              Common Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-[#EDE6DA] rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
                >
                  <span className="text-[#2C2825] text-sm pr-4" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}>
                    {faq.q}
                  </span>
                  <span className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className={`ri-${openFaq === i ? "subtract" : "add"}-line text-[#9E8070] text-base`} />
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-[#8B8378] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
